import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../shadcn/ui/Dialog";
import { Button } from "../../../shadcn/ui/Button";
import { Label } from "../../../shadcn/ui/Label";
import { Input } from "../../../shadcn/ui/Input";
import { Textarea } from "../../../shadcn/ui/Textarea";
import { rfqService, adminService } from "@b2b/services";
import { XCircle } from "lucide-react";

interface QuoteRevisionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rfqId: string;
  quoteId: string;
  currentPrice: number;
  currentCurrency: string;
  currentLeadTime: number;
  currentMessage?: string;
  onRevisionCreated?: () => void;
}

export const QuoteRevisionDialog = ({
  open,
  onOpenChange,
  rfqId,
  quoteId,
  currentPrice,
  currentCurrency,
  currentLeadTime,
  currentMessage,
  onRevisionCreated,
}: QuoteRevisionDialogProps) => {
  const [price, setPrice] = useState<string>(currentPrice.toString());
  const [currency, setCurrency] = useState<string>(currentCurrency);
  const [leadTimeDays, setLeadTimeDays] = useState<string>(currentLeadTime.toString());
  const [message, setMessage] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setPrice(currentPrice.toString());
      setCurrency(currentCurrency);
      setLeadTimeDays(currentLeadTime.toString());
      setMessage("");
      setFiles([]);
    }
  }, [open, currentPrice, currentCurrency, currentLeadTime]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = selectedFiles.filter((file) => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert(`File ${file.name} exceeds 10MB size limit`);
        return false;
      }
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} has invalid type. Allowed: PDF, PNG, JPG, XLSX`);
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Upload files first
      const attachments: string[] = [];
      if (files.length > 0) {
        setUploadingFiles(files.map((f) => f.name));
        for (const file of files) {
          try {
            const result = await adminService.uploadMedia(file);
            if (result?.id) {
              attachments.push(result.id);
            }
          } catch (error: any) {
            console.error(`Error uploading file ${file.name}:`, error);
          }
        }
        setUploadingFiles([]);
      }

      // Create revision
      await rfqService.reviseQuote(rfqId, quoteId, {
        price: parseFloat(price) || currentPrice,
        currency: currency || currentCurrency,
        leadTimeDays: parseInt(leadTimeDays) || currentLeadTime,
        message: message || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      onRevisionCreated?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error creating revision:", error);
      alert(error?.response?.data?.message || "Failed to create revision");
    } finally {
      setSubmitting(false);
      setUploadingFiles([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Revise Quote</DialogTitle>
          <DialogDescription>
            Update your quote details. Changes will be visible to the buyer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price">Price *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="currency">Currency *</Label>
              <Input
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                placeholder="USD"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="leadTime">Lead Time (Days) *</Label>
            <Input
              id="leadTime"
              type="number"
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(e.target.value)}
              placeholder="0"
            />
          </div>

          <div>
            <Label htmlFor="message">Message (Optional)</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Explain the changes in this revision..."
              rows={4}
            />
          </div>

          <div>
            <Label htmlFor="attachments">Attachments (Optional)</Label>
            <Input
              id="attachments"
              type="file"
              multiple
              onChange={handleFileSelect}
              accept=".pdf,.png,.jpg,.jpeg,.xlsx"
              className="mt-1"
            />
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                    <span>{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {uploadingFiles.length > 0 && (
              <div className="mt-2 text-sm text-muted-foreground">
                Uploading: {uploadingFiles.join(", ")}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !price || !leadTimeDays}
            variant="primary"
          >
            {submitting ? "Submitting..." : "Submit Revision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRevisionDialog;

